import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Locale } from "../shared/dto";
import { VoiceableInput, type VoiceRecordingState } from "./VoiceableInput";
import { isRequiredAnswerComplete, missingRequiredAnswers } from "../shared/questionnaire-completeness";
import { isQuestionVisible, type QuestionCondition } from "../shared/questionnaire-conditions";
import { answerBody } from "./staff-answers";
import { ProfileConfirmationGate } from "./ProfileView";
import { marketCurrency, type MarketCurrency } from "../shared/market-currency";
import { resolveAnswerConflict } from "./questionnaire-save";
import { BRAND_UPLOAD_MIME_TYPES } from "./attachment-upload";
import { DesignInputsField, FileListField, VoiceTextField } from "./questionnaire-fields";
import { LockedStep } from "./sections/ClosedStep";
import { actionIssueFrom } from "./action-error";
import type { RailTarget } from "./rail-model";

type QuestionnaireKey = "q1" | "q2";
export type QuestionnaireSubmitTarget = "strategy" | "next";
type SaveState = "idle" | "dirty" | "saving" | "saved" | "offline" | "conflict" | "error";
export type QuestionnaireSubmitState = "idle" | "submitting" | "waiting" | "ready" | "error" | "dossier_pending";

export { classifyAnswerConflict } from "./questionnaire-save";

export function nextSaveDisplayState(
  previous: SaveState | null,
  actual: SaveState,
  savingDelayElapsed: boolean
): SaveState | null {
  if (actual === "idle") return null;
  if (actual === "dirty") return previous === "saved" ? "saved" : null;
  if (actual === "saving" && !savingDelayElapsed) return previous === "saved" ? "saved" : null;
  return actual;
}

export function questionnaireVoiceRecommendationCopy(t: (key: string) => string): string {
  return t("questionnaire.voiceRecommendationCopy");
}

export const MOBILE_QUESTION_STICKY_GAP = 12;

/**
 * How far the page must still move so the heading clears the sticky row.
 * Negative = scroll up by that many pixels; 0 = nothing is covered. Exact
 * coordinates, no guessed constants (fixr4, a1-r4-02).
 */
export function stickyOverlapCorrection(
  stickyBottom: number,
  headingTop: number,
  gap = MOBILE_QUESTION_STICKY_GAP
): number {
  const requiredTop = stickyBottom + gap;
  return headingTop < requiredTop ? Math.round(headingTop - requiredTop) : 0;
}

/**
 * fixr3 subtracted the sticky nav height it measured in the SAME frame the
 * next question mounted, so an answered question's taller summary still
 * covered the heading (a1-r4-02). New approach: the sticky height feeds a
 * CSS variable consumed by scroll-margin-top, the browser positions the
 * question itself, and a settle frame corrects with the REAL post-scroll
 * coordinates.
 */
export function scrollMobileQuestionIntoView(key: string): void {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(`[data-question-key="${CSS.escape(key)}"]`);
      if (!target) return;
      const nav = document.querySelector<HTMLElement>(".mobile-question-nav");
      const stickyHeight = Math.ceil(nav?.getBoundingClientRect().height ?? 0);
      document.documentElement.style.setProperty(
        "--mobile-question-scroll-offset",
        `${stickyHeight + MOBILE_QUESTION_STICKY_GAP}px`
      );
      target.scrollIntoView({ block: "start" });
      window.requestAnimationFrame(() => {
        const stickyBottom = nav?.getBoundingClientRect().bottom ?? 0;
        const correction = stickyOverlapCorrection(stickyBottom, target.getBoundingClientRect().top);
        if (correction !== 0) window.scrollBy({ top: correction, behavior: "auto" });
      });
    });
  });
}

function useCalmSaveState(actual: SaveState): SaveState | null {
  const [display, setDisplay] = useState<SaveState | null>(() => nextSaveDisplayState(null, actual, false));
  useEffect(() => {
    setDisplay((previous) => nextSaveDisplayState(previous, actual, false));
    if (actual !== "saving") return;
    const timer = window.setTimeout(() => {
      setDisplay((previous) => nextSaveDisplayState(previous, actual, true));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [actual]);
  return display;
}

function SaveStatus(props: {
  state: SaveState;
  isRecording: boolean;
  t(key: string): string;
}) {
  const display = useCalmSaveState(props.state);
  const hidden = display === null;
  const icon = display === "saved" ? "✓" : display === "error" || display === "offline" || display === "conflict" ? "!" : "•";
  return (
    <p
      className={`save-state ${display ?? "idle"}${hidden ? " is-hidden" : ""}${props.isRecording ? " is-recording" : ""}`}
      aria-live="polite"
      aria-hidden={props.isRecording || hidden}
    >
      <span className="save-state-icon" aria-hidden="true">{icon}</span>
      <span>{display ? props.t(`questionnaire.save.${display}`) : "\u00a0"}</span>
    </p>
  );
}

export function questionnaireProgressHint(
  state: QuestionnaireSubmitState,
  t: (key: string) => string
): string {
  return t(state === "waiting" || state === "ready" ? "questionnaire.closedHint" : "questionnaire.saveHint");
}

interface QuestionItem {
  key: string;
  type: "confirm" | "voice_or_text" | "repeat_voice_or_text" | "text" | "choice" | "choice_or_text";
  required: boolean;
  questionVersion: 1 | 2;
  group?: string;
  question: string;
  showIf?: QuestionCondition;
  minEntries?: number;
  repeatVariant?: "real_vs_estimate";
  linkedFrom?: string;
  /** F168: the existing Buddy's Telegram username, shown locked under q1_07. */
  lockedBotUsername?: string;
}

interface PrefillFact {
  id: string;
  factKey: string;
  category: string;
  effectiveText: string;
  source: unknown;
  included: boolean;
  action: "confirm" | "correct" | "irrelevant" | null;
}

interface AnswerDto {
  itemKey: string;
  value: Record<string, unknown>;
  revision: number;
  attachmentId: string | null;
  transcriptStatus: string | null;
  transcript?: string;
  linked?: boolean;
  readOnly?: boolean;
}

interface QuestionnaireResponse {
  testUnlocked?: boolean;
  questionnaire: QuestionnaireKey;
  /**
   * F116: the unit for the price question, decided by the SERVER from
   * `clients.market_language`. Optional so a page still holding an older
   * response keeps working; the fallback derives it from the interface
   * language, which is right far more often than the hardcoded "CZK" was.
   */
  currency?: MarketCurrency;
  items: QuestionItem[];
  answers: AnswerDto[];
  prefill: Record<string, PrefillFact[]>;
  submission: { status: "draft" | "waiting_for_transcripts" | "ready" | "exported" } | null;
  /** F104: the partner's written reason when they returned the submission. */
  staffReturned?: string | null;
}

interface AnswerDraft {
  value: Record<string, unknown>;
  revision: number;
  attachmentId: string | null;
  transcriptStatus: string | null;
  transcript: string | null;
  sourceFactIds: string[];
  factUpdates: Array<{
    factId: string;
    action: "confirm" | "correct" | "irrelevant";
    text?: string;
    attachmentId?: string;
  }>;
  saveState: SaveState;
  serverRevision?: number;
  linked?: boolean;
  readOnly?: boolean;
}

interface QuestionnaireRunnerProps {
  questionnaire: QuestionnaireKey;
  csrfToken: string;
  locale: Locale;
  draftNamespace: string;
  // Test walkthrough (dev only): asks the server to open a still-locked
  // questionnaire for real filling (?test=1); submission stays production-gated.
  testMode?: boolean;
  /**
   * N01: where this step sits in THIS client's rail. The questionnaire was the
   * one step page with no anatomy at all - no back link, no eyebrow, no h1 -
   * so a 21 241 px surface never said what the client was filling in or where
   * they were. Every other step page (LockedStep, ClosedStep, the sales pages)
   * has carried this header for weeks. Without a position the eyebrow is
   * simply left out; the heading is not.
   */
  position?: { current: number; total: number } | null;
  t(key: string): string;
  onClose(): void;
  onOpenProfile?(): void;
  onComplete(): Promise<void>;
  /**
   * Where the client goes after a submitted questionnaire. "strategy" is q2's
   * fixed destination; "next" hands the routing to the rail, which knows the
   * first step that is actually open (fix-q1flow F47 - q1 used to submit into
   * silence and leave the client staring at the page they just finished).
   */
  /**
   * `transcriptsPending` is the REASON the client may still be held here, and
   * it comes from the submission status, never guessed from the rail: a
   * celebration that says "we are still writing up your voice notes" when the
   * transcripts are long finished is simply a lie to the client.
   */
  onSubmitted?(target: QuestionnaireSubmitTarget, info: { transcriptsPending: boolean }): void | Promise<void>;
  onTestAdvance?(): void | Promise<void>;
  closedStep?: ReactNode;
  /**
   * 🔴 F147: the step that IS on the client's plate, so a REFUSED
   * questionnaire is not a dead end. Pavel walked into one: the page carried
   * a single sentence ("Odemkne se po uvodnim hovoru" - a call F81 retired)
   * and one "back home" button, so the only thing the product told him was to
   * wait for a meeting nobody will ever schedule. It is the same pair
   * `LockedStep` already takes for every OTHER locked step of the journey.
   */
  next?: { target: RailTarget; title: string } | null;
  onOpenStep?(target: RailTarget): void;
}

export function QuestionnaireRunner(props: QuestionnaireRunnerProps) {
  const [data, setData] = useState<QuestionnaireResponse | null>(null);
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const [profileGate, setProfileGate] = useState(false);
  // Questions answered before this visit start collapsed; anything the client
  // touches in this session stays open while they work on it.
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [submitState, setSubmitState] = useState<QuestionnaireSubmitState>("idle");
  // F89/F44: which sentence the error state shows. Only a genuine
  // required-answers refusal may blame the answers; any other failure after a
  // passing completeness check is our fault and must say so.
  const [submitErrorKey, setSubmitErrorKey] = useState("questionnaire.submitTechnicalError");
  const [recordingSources, setRecordingSources] = useState<Map<string, string>>(new Map());
  const [mobileQuestionnaire, setMobileQuestionnaire] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const [mobileQuestionKey, setMobileQuestionKey] = useState<string | null>(null);
  const [voiceHelpOpen, setVoiceHelpOpen] = useState(() =>
    typeof window === "undefined" || !window.matchMedia("(max-width: 767px)").matches
  );
  const draftsRef = useRef<Record<string, AnswerDraft>>({});
  const saveGenerations = useRef(new Map<string, number>());
  const inFlightSaves = useRef(new Map<string, Promise<boolean>>());
  const submitCommandId = useRef<string | null>(null);

  const testQuery = props.testMode ? "?test=1" : "";

  /**
   * The save loop reads draftsRef right after every await, so the ref has to be
   * current AT THAT MOMENT. Writing it inside a setDrafts updater made it lag a
   * render behind whenever two draft writes queued before React flushed - which
   * is exactly what a voice answer does (transcript, then the archived
   * recording) - and the follow-up save then went out with a spent revision
   * (fix-q1flow F43). One commit point: ref first, state after.
   */
  const commitDrafts = useCallback((
    mutate: (current: Record<string, AnswerDraft>) => Record<string, AnswerDraft>
  ) => {
    const next = mutate(draftsRef.current);
    draftsRef.current = next;
    setDrafts(next);
    return next;
  }, []);

  const patchDraft = useCallback((itemKey: string, patch: Partial<AnswerDraft>) => {
    commitDrafts((all) => all[itemKey] ? { ...all, [itemKey]: { ...all[itemKey], ...patch } } : all);
  }, [commitDrafts]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => {
      setMobileQuestionnaire(query.matches);
      setVoiceHelpOpen(!query.matches);
    };
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setLockedOut(false);
    setProfileGate(false);
    try {
      const response = await fetch(`/api/questionnaires/${props.questionnaire}${testQuery}`, { credentials: "include" });
      // A locked questionnaire is a normal state, not a loading failure
      // (Pavel saw "obsah se nepodařilo načíst" on a not-yet-unlocked step).
      // 🔴 F147: read the CODE, never the status alone. A 403 is "this step is
      // not open yet" AND "your session may not do this" AND a CSRF/origin
      // refusal, and the locked page's sentence used to print over all three -
      // so a client whose session had expired was told to finish an earlier
      // step. `actionIssueFrom` reads the body once and never throws.
      if (response.status === 403 || response.status === 409) {
        const issue = await actionIssueFrom(response);
        if (issue === "questionnaire_locked") { setLockedOut(true); return; }
        if (issue === "profile_confirmation_required") { setProfileGate(true); return; }
        throw new Error("questionnaire_load_failed");
      }
      if (!response.ok) throw new Error("questionnaire_load_failed");
      const loaded = await response.json() as QuestionnaireResponse;
      const nextDrafts: Record<string, AnswerDraft> = {};
      for (const item of loaded.items) {
        const answer = loaded.answers.find((candidate) => candidate.itemKey === item.key);
        const local = answer?.readOnly
          ? null
          : await readLocalDraft(localDraftKey(props.draftNamespace, props.questionnaire, item.key));
        nextDrafts[item.key] = local ?? {
          value: answer?.value ?? defaultValue(item),
          revision: answer?.revision ?? 0,
          attachmentId: answer?.attachmentId ?? null,
          transcriptStatus: answer?.transcriptStatus ?? null,
          transcript: answer?.transcript ?? null,
          sourceFactIds: loaded.prefill[item.key]?.map((fact) => fact.id) ?? [],
          factUpdates: [],
          saveState: answer ? "saved" : "idle",
          linked: answer?.linked,
          readOnly: answer?.readOnly
        };
      }
      setData(loaded);
      draftsRef.current = nextDrafts;
      setDrafts(nextDrafts);
      if (loaded.submission?.status === "waiting_for_transcripts") setSubmitState("waiting");
      if (loaded.submission && ["ready", "exported"].includes(loaded.submission.status)) setSubmitState("ready");
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [props.draftNamespace, props.questionnaire, testQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  const currentAnswerValues = Object.fromEntries(
    Object.entries(drafts).map(([key, draft]) => [key, draft.value])
  );
  const dirtyKey = data?.items.find((item) =>
    isQuestionVisible(item, currentAnswerValues) && drafts[item.key]?.saveState === "dirty"
  )?.key ?? null;
  useEffect(() => {
    if (!dirtyKey) return;
    const timer = window.setTimeout(() => void saveItem(dirtyKey), 800);
    return () => window.clearTimeout(timer);
  }, [dirtyKey, drafts]);

  useEffect(() => {
    if (submitState !== "waiting") return;
    const timer = window.setInterval(async () => {
      const response = await fetch(`/api/questionnaires/${props.questionnaire}/submission`, { credentials: "include" });
      if (!response.ok) return;
      const status = (await response.json() as { status: string }).status;
      if (status === "ready" || status === "exported") {
        setSubmitState("ready");
        window.clearInterval(timer);
        // The transcripts finished after the submit: the step that was still
        // locked is open now, so move the client on instead of leaving them
        // on a finished questionnaire (F47).
        if (props.onSubmitted) await props.onSubmitted(questionnairePostSubmitTarget(props.questionnaire), { transcriptsPending: false });
        else await props.onComplete();
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [props.onComplete, props.questionnaire, submitState]);

  const updateDraft = useCallback((itemKey: string, update: Partial<AnswerDraft>) => {
    setOpenKeys((current) => current.has(itemKey) ? current : new Set([...current, itemKey]));
    commitDrafts((current) => {
      const existing = current[itemKey];
      if (!existing || existing.readOnly) return current;
      const next = { ...existing, ...update, saveState: "dirty" as const };
      saveGenerations.current.set(itemKey, (saveGenerations.current.get(itemKey) ?? 0) + 1);
      void writeLocalDraft(localDraftKey(props.draftNamespace, props.questionnaire, itemKey), next);
      return { ...current, [itemKey]: next };
    });
  }, [commitDrafts, props.draftNamespace, props.questionnaire]);

  const updateRecordingState = useCallback((itemKey: string, state: VoiceRecordingState) => {
    setRecordingSources((current) => {
      const next = new Map(current);
      if (state.recording) next.set(state.sourceId, itemKey);
      else next.delete(state.sourceId);
      return next;
    });
  }, []);

  async function saveItem(itemKey: string): Promise<boolean> {
    const running = inFlightSaves.current.get(itemKey);
    if (running) return running;
    const current = draftsRef.current[itemKey];
    if (current?.readOnly) return true;
    if (!current || !["dirty", "offline", "error"].includes(current.saveState)) return true;
    const generation = saveGenerations.current.get(itemKey) ?? 0;
    patchDraft(itemKey, { saveState: "saving" });
    const operation = (async () => {
      try {
      const response = await fetch(`/api/questionnaires/${props.questionnaire}/answers/${encodeURIComponent(itemKey)}${testQuery}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "If-Match": String(current.revision)
        },
        body: JSON.stringify({
          value: current.value,
          attachmentId: current.attachmentId,
          sourceFactIds: current.sourceFactIds,
          factUpdates: current.factUpdates
        })
      });
      const body = await response.json() as {
        revision?: number;
        error?: string;
        serverRevision?: number;
        serverValue?: Record<string, unknown> | null;
        serverAttachmentId?: string | null;
      };
      if (response.status === 409 && body.error === "answer_changed") {
        const outcome = resolveAnswerConflict({
          localRevision: current.revision,
          sent: { value: current.value, attachmentId: current.attachmentId },
          server: {
            revision: body.serverRevision,
            value: body.serverValue,
            attachmentId: body.serverAttachmentId
          }
        });
        // Our own earlier write already landed (voice: transcript first, the
        // archived recording a moment later). Adopt the revision in silence -
        // there is no other window and nothing to reload.
        if (outcome.kind === "already_saved") {
          const unchangedAfterSave = generation === (saveGenerations.current.get(itemKey) ?? 0);
          patchDraft(itemKey, {
            revision: outcome.revision,
            saveState: unchangedAfterSave ? "saved" : "dirty",
            ...(unchangedAfterSave ? { factUpdates: [] } : {})
          });
          if (unchangedAfterSave) {
            await deleteLocalDraft(localDraftKey(props.draftNamespace, props.questionnaire, itemKey));
          }
          return true;
        }
        patchDraft(itemKey, {
          saveState: outcome.kind === "conflict" ? "conflict" : "error",
          serverRevision: body.serverRevision
        });
        return false;
      }
      if (response.status === 409 && body.error === "state_conflict" && !itemVisibleNow(itemKey)) {
        // A branch can close while its last answer is still debouncing. Keep
        // that local draft dirty so it will save if the branch opens again,
        // but do not block close or submit while the item is hidden.
        patchDraft(itemKey, { saveState: "dirty" });
        return true;
      }
      if (!response.ok || typeof body.revision !== "number") throw new Error("save_failed");
      const unchanged = generation === (saveGenerations.current.get(itemKey) ?? 0);
      patchDraft(itemKey, {
        revision: body.revision,
        saveState: unchanged ? "saved" : "dirty",
        ...(unchanged ? { factUpdates: [] } : {})
      });
      if (unchanged) {
        await deleteLocalDraft(localDraftKey(props.draftNamespace, props.questionnaire, itemKey));
      }
      return true;
      } catch {
      patchDraft(itemKey, { saveState: navigator.onLine ? "error" : "offline" });
      return false;
      }
    })();
    inFlightSaves.current.set(itemKey, operation);
    const result = await operation;
    if (inFlightSaves.current.get(itemKey) === operation) inFlightSaves.current.delete(itemKey);
    if (result && generation !== (saveGenerations.current.get(itemKey) ?? 0)) return saveItem(itemKey);
    return result;
  }

  async function loadServerAnswer(itemKey: string): Promise<void> {
    try {
      const response = await fetch(`/api/questionnaires/${props.questionnaire}${testQuery}`, { credentials: "include" });
      if (!response.ok) throw new Error("questionnaire_load_failed");
      const loaded = await response.json() as QuestionnaireResponse;
      const item = loaded.items.find((candidate) => candidate.key === itemKey);
      if (!item) throw new Error("questionnaire_item_missing");
      const answer = loaded.answers.find((candidate) => candidate.itemKey === itemKey);
      const replacement: AnswerDraft = {
        value: answer?.value ?? defaultValue(item),
        revision: answer?.revision ?? 0,
        attachmentId: answer?.attachmentId ?? null,
        transcriptStatus: answer?.transcriptStatus ?? null,
        transcript: answer?.transcript ?? null,
        sourceFactIds: loaded.prefill[itemKey]?.map((fact) => fact.id) ?? [],
        factUpdates: [],
        saveState: answer ? "saved" : "idle",
        linked: answer?.linked,
        readOnly: answer?.readOnly
      };
      await deleteLocalDraft(localDraftKey(props.draftNamespace, props.questionnaire, itemKey));
      saveGenerations.current.delete(itemKey);
      setData(loaded);
      commitDrafts((all) => ({ ...all, [itemKey]: replacement }));
    } catch {
      patchDraft(itemKey, { saveState: "error" });
    }
  }

  async function saveAllDirty(): Promise<boolean> {
    while (true) {
      if (inFlightSaves.current.size > 0) {
        const results = await Promise.all([...inFlightSaves.current.values()]);
        if (results.some((result) => !result)) return false;
      }
      const pending = (data?.items ?? []).filter((it) =>
        itemVisibleNow(it.key) &&
        ["dirty", "offline", "error"].includes(draftsRef.current[it.key]?.saveState ?? "")
      );
      if (pending.length === 0) return true;
      const results = await Promise.all(pending.map((it) => saveItem(it.key)));
      if (results.some((result) => !result)) return false;
    }
  }

  function itemVisibleNow(itemKey: string): boolean {
    const item = data?.items.find((candidate) => candidate.key === itemKey);
    if (!item) return true;
    const values = Object.fromEntries(
      Object.entries(draftsRef.current).map(([key, draft]) => [key, draft.value])
    );
    return isQuestionVisible(item, values);
  }

  async function close() {
    if (!(await saveAllDirty())) return;
    props.onClose();
  }

  async function submit() {
    if (!(await saveAllDirty())) return;
    await routeQuestionnaireSubmit(Boolean(props.testMode), props.onTestAdvance, async () => {
      setSubmitState("submitting");
      setSubmitErrorKey("questionnaire.submitTechnicalError");
      try {
        submitCommandId.current ??= crypto.randomUUID();
        const response = await fetch(`/api/questionnaires/${props.questionnaire}/submit`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Launchpad-CSRF": props.csrfToken,
            "Idempotency-Key": submitCommandId.current
          },
          body: "{}"
        });
        const body = await response.json() as { status?: string; error?: string };
        if (!response.ok) {
          if (body.error === "dossier_not_ready") {
            setSubmitState("dossier_pending");
            return;
          }
          if (body.error === "required_answers_missing") setSubmitErrorKey("questionnaire.submitError");
          throw new Error("submit_failed");
        }
        await clearQuestionnaireDrafts(props.draftNamespace);
        submitCommandId.current = null;
        const nextState = body.status === "waiting_for_transcripts" ? "waiting" : "ready";
        setSubmitState(nextState);
        // No silent ending: hand the client to the next open step even when
        // the transcripts are still running (F47).
        if (props.onSubmitted) await props.onSubmitted(questionnairePostSubmitTarget(props.questionnaire), { transcriptsPending: nextState === "waiting" });
        else await props.onComplete();
      } catch {
        // A lost response can arrive after the server committed the transition.
        // Re-read the authoritative state before presenting a retry error.
        try {
          const statusResponse = await fetch(`/api/questionnaires/${props.questionnaire}/submission`, { credentials: "include" });
          if (statusResponse.ok) {
            const status = (await statusResponse.json() as { status: string }).status;
            if (status === "waiting_for_transcripts") {
              await clearQuestionnaireDrafts(props.draftNamespace);
              submitCommandId.current = null;
              setSubmitState("waiting");
              if (props.onSubmitted) await props.onSubmitted(questionnairePostSubmitTarget(props.questionnaire), { transcriptsPending: true });
              else await props.onComplete();
              return;
            }
            if (status === "ready" || status === "exported") {
              await clearQuestionnaireDrafts(props.draftNamespace);
              submitCommandId.current = null;
              setSubmitState("ready");
              if (props.onSubmitted) await props.onSubmitted(questionnairePostSubmitTarget(props.questionnaire), { transcriptsPending: false });
              else await props.onComplete();
              return;
            }
          }
        } catch {
          // Keep the original ambiguous failure and allow retry with the same key.
        }
        setSubmitState("error");
      }
    });
  }

  if (loading) return <QuestionnaireLoadingState t={props.t} />;
  // 🔴 F147: a refused questionnaire is a STEP PAGE, not a sentence in an
  // empty frame. Both refusals keep the one navigation certainty every other
  // subpage has (the back link) and both name the way forward: the profile
  // gate points at the profile, the locked step at whatever the client can
  // really do now. Pavel's walk ended in the second one, which carried a
  // single sentence about an onboarding CALL that F81 had retired.
  if (profileGate) {
    return (
      <main className="questionnaire-state questionnaire-profile-gate">
        <button className="surface-back" type="button" onClick={props.onClose}>{props.t("common.backHome")}</button>
        <ProfileConfirmationGate t={props.t} onOpenProfile={props.onOpenProfile ?? props.onClose} />
      </main>
    );
  }
  if (lockedOut) {
    return (
      <main className="questionnaire-state questionnaire-locked-state">
        <LockedStep
          step={props.questionnaire}
          t={props.t}
          copyKey="rail.unlock.stepLockedLead"
          onBack={props.onClose}
          position={props.position ?? undefined}
          next={props.next ?? undefined}
          onContinue={props.onOpenStep}
        />
      </main>
    );
  }
  if (loadError || !data) {
    return <main className="questionnaire-state"><p className="error-message">{props.t("questionnaire.loadError")}</p><button className="primary-button compact-button" onClick={() => void load()}>{props.t("common.retry")}</button></main>;
  }
  // All questions on one page: answered ones tick off and collapse, the bar on
  // top fills up (Pavel: the page should visibly fill as I answer). After
  // submission there is NO takeover statement - the same page stays, locked,
  // with a quiet status line on top.
  // Test walkthrough of a still-locked questionnaire (dev only): the server
  // opened it for real filling; only the submit gate keeps production order.
  const testUnlocked = Boolean(data.testUnlocked);
  const locked = submitState === "waiting" || submitState === "ready";
  const saved = (key: string) => {
    const d = drafts[key];
    return Boolean(d && (d.revision > 0 || d.saveState === "saved")) || locked;
  };
  const answerValues = Object.fromEntries(Object.entries(drafts).map(([key, draft]) => [key, draft.value]));
  const visibleItems = data.items.filter((item) => isQuestionVisible(item, answerValues));
  // F85: ONE truth of "answered". A required question counts as answered only
  // when its saved value satisfies the shared completeness rule - a collapsed
  // ZODPOVĚZENO badge over an empty required follow-up (q2_m08) is exactly how
  // the green "Máš hotovo" ended up above a disabled submit button.
  const answered = (item: QuestionItem) => saved(item.key) && (locked || !item.required ||
    isRequiredAnswerComplete(item, drafts[item.key] ?? null, data.prefill[item.key]?.length ?? 0));
  const doneCount = visibleItems.filter((it) => answered(it)).length;
  const total = visibleItems.length;
  const allDone = doneCount === total;
  const defaultMobileQuestion = visibleItems.find((item) => !answered(item))?.key ?? visibleItems[0]?.key ?? null;
  const activeMobileQuestion = visibleItems.some((item) => item.key === mobileQuestionKey)
    ? mobileQuestionKey
    : defaultMobileQuestion;
  const mobileQuestionIndex = Math.max(0, visibleItems.findIndex((item) => item.key === activeMobileQuestion));
  const selectMobileQuestion = (key: string) => {
    setMobileQuestionKey(key);
    scrollMobileQuestionIntoView(key);
  };
  // The answers save themselves; the button is not a "send" but a gate to the
  // next step. It stays grey until every required question is answered
  // (Pavel: "přejít k dalšímu kroku, zašedlé, dokud nemám vyplněné otázky").
  // F85: the gap list is the ONE shared derivation - headline, hint and
  // button all read it, so they can never contradict each other again, and
  // the hint names the blocking questions instead of gesturing upwards (F44).
  const missingRequired = locked ? [] : missingRequiredAnswers(
    visibleItems,
    answerValues,
    (it) => drafts[it.key] ?? null,
    (it) => data.prefill[it.key]?.length ?? 0
  );
  const missingSummary = missingRequired.map((item) => ({
    index: visibleItems.indexOf(item) + 1,
    title: clientQuestionText(item, props.t)
  }));
  return (
    <main className="questionnaire-shell q-list">
      {/* The one navigation certainty every other subpage has (K05 gave it to
          the locked page; the questionnaire never had it either). */}
      <button className="surface-back" type="button" onClick={props.onClose}>
        {props.t("common.backHome")}
      </button>
      {/* A finished questionnaire is titled by its ClosedStep card, so the page
          keeps exactly ONE h1 either way (the N02 defect, not repeated here). */}
      {!props.closedStep && (
        <header className="questionnaire-page-heading">
          {props.position && (
            <p className="eyebrow blue-eyebrow">
              {props.t("phase.progress")
                .replace("{current}", String(props.position.current))
                .replace("{total}", String(props.position.total))}
            </p>
          )}
          <h1 id="dashboard-title">{props.t(`rail.steps.${props.questionnaire}`)}</h1>
          <p className="lead">{props.t(`sections.${props.questionnaire}.actionCopy`)}</p>
          {/* F104: the partner returned the submission - the client reads the
              written reason right where they continue working. The server
              sends it only while the submission is really back in draft. */}
          {data?.staffReturned && (
            <p className="message staff-returned-note" role="status">
              {props.t("questionnaire.staffReturned").replace("{reason}", data.staffReturned)}
            </p>
          )}
        </header>
      )}
      {locked && props.closedStep}
      {testUnlocked && (
        <p className="q-list-locked" role="status">{props.t("questionnaire.testOpenNote")}</p>
      )}
      {locked && !props.closedStep && (
        <QuestionnaireSubmitBlock
          questionnaire={props.questionnaire}
          submitState={submitState}
          submitErrorKey={submitErrorKey}
          allDone={allDone}
          missing={missingSummary}
          t={props.t}
          onSubmit={() => void submit()}
        />
      )}
      <div className="q-list-progress">
        <div className="questionnaire-progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={doneCount}>
          <span style={{ width: `${(doneCount / Math.max(1, total)) * 100}%` }} />
        </div>
        <p className="q-list-progress-copy">
          {props.t("questionnaire.list.progress").replace("{done}", String(doneCount)).replace("{total}", String(total))}
          <span className="questionnaire-save-hint">{questionnaireProgressHint(submitState, props.t)}</span>
        </p>
      </div>
      <aside className="q-voice-recommendation">
        <button
          className="q-voice-recommendation-toggle"
          type="button"
          aria-expanded={voiceHelpOpen}
          onClick={() => setVoiceHelpOpen((open) => !open)}
        >
          <strong>{props.t("questionnaire.voiceRecommendationTitle")}</strong>
          <span aria-hidden="true">{voiceHelpOpen ? "−" : "+"}</span>
        </button>
        <p hidden={!voiceHelpOpen}>{questionnaireVoiceRecommendationCopy(props.t)}</p>
      </aside>
      {mobileQuestionnaire && activeMobileQuestion && (
        <nav className="mobile-question-nav" aria-label={props.t("questionnaire.mobile.navigationLabel")}>
          <details>
            <summary>
              {props.t("questionnaire.mobile.position")
                .replace("{current}", String(mobileQuestionIndex + 1))
                .replace("{total}", String(total))}
            </summary>
            <ol className="mobile-question-nav-list">
              {visibleItems.map((item, index) => (
                <li key={item.key}>
                  <button type="button" className={item.key === activeMobileQuestion ? "active" : ""} aria-current={item.key === activeMobileQuestion ? "step" : undefined} onClick={() => selectMobileQuestion(item.key)}>
                    <span>{answered(item) ? "✓" : index + 1}</span>
                    {clientQuestionText(item, props.t)}
                  </button>
                </li>
              ))}
            </ol>
          </details>
        </nav>
      )}
      <ol className="q-items">
        {visibleItems.map((it, itemIndex) => {
          const draft = drafts[it.key];
          if (!draft) return null;
          const isAnswered = answered(it);
          const isRecording = [...recordingSources.values()].includes(it.key);
          const isOpen = locked
            ? openKeys.has(it.key)
            : openKeys.has(it.key) || !isAnswered;
          const groupStarts = Boolean(it.group) && visibleItems[itemIndex - 1]?.group !== it.group;
          return (
            <Fragment key={it.key}>
            {groupStarts && (
              <li className={`q-group-head${mobileQuestionnaire && it.key !== activeMobileQuestion ? " mobile-hidden" : ""}`} role="presentation" aria-hidden="true">
                <span className="q-group-title">{props.t(`questionnaire.groups.${it.group}.title`)}</span>
                <span className="q-group-copy">{props.t(`questionnaire.groups.${it.group}.copy`)}</span>
              </li>
            )}
            <li data-question-key={it.key} className={`q-item ${isAnswered ? "done" : ""} ${isOpen ? "open" : ""}${mobileQuestionnaire && it.key !== activeMobileQuestion ? " mobile-hidden" : ""}`}>
              <div className="q-item-head">
                <span className="q-item-mark" aria-hidden="true">{isAnswered ? "✓" : itemIndex + 1}</span>
                <h2 tabIndex={-1}>{clientQuestionText(it, props.t)}</h2>
                {isAnswered && <span className="q-item-chip status-pill pill-done">{props.t("questionnaire.list.answered")}</span>}
                {isAnswered && !isOpen && !locked && (
                  <button className="quiet-link q-item-edit" type="button" onClick={() => setOpenKeys(new Set([...openKeys, it.key]))}>
                    {props.t("questionnaire.list.edit")}
                  </button>
                )}
                {locked && (
                  <button
                    className="quiet-link q-item-edit"
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenKeys((current) => {
                      const next = new Set(current);
                      if (next.has(it.key)) next.delete(it.key);
                      else next.add(it.key);
                      return next;
                    })}
                  >
                    {props.t(isOpen ? "questionnaire.list.hideAnswer" : "questionnaire.list.viewAnswer")}
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="q-item-body">
                  {/* F86: a linked prefill says where it came from and stays
                      editable - the locked quote with no editor is gone. */}
                  {draft.linked && !locked && !draft.readOnly && (
                    <p className="q-linked-note">{props.t("questionnaire.list.linkedPrefill")}</p>
                  )}
                  {/* F168: the Buddy already exists, its username is not a question. */}
                  {it.lockedBotUsername && (
                    <p className="q-linked-note">
                      {props.t("questionnaire.help.existingBotUsername").replace("{username}", `@${it.lockedBotUsername}`)}
                    </p>
                  )}
                  {locked || draft.readOnly ? (
                    <ReadOnlyAnswer draft={draft} t={props.t} />
                  ) : (
                    <QuestionField
                      item={it}
                      draft={draft}
                      facts={data.prefill[it.key] ?? []}
                      scope={`${props.questionnaire}_voice` as "q1_voice" | "q2_voice"}
                      currency={data.currency ?? marketCurrency(props.locale)}
                      csrfToken={props.csrfToken}
                      t={props.t}
                      update={(update) => updateDraft(it.key, update)}
                      onRecordingStateChange={(state) => updateRecordingState(it.key, state)}
                    />
                  )}
                  {!locked && <SaveStatus state={draft.saveState} isRecording={isRecording} t={props.t} />}
                  {draft.saveState === "conflict" && (
                    <button className="quiet-link save-conflict-action" type="button" onClick={() => void loadServerAnswer(it.key)}>
                      {props.t("questionnaire.loadNewerAnswer")}
                    </button>
                  )}
                </div>
              )}
            </li>
            </Fragment>
          );
        })}
      </ol>
      {mobileQuestionnaire && activeMobileQuestion && (
        <div className="mobile-question-controls">
          <button className="secondary-button" type="button" disabled={mobileQuestionIndex === 0} onClick={() => selectMobileQuestion(visibleItems[mobileQuestionIndex - 1]?.key ?? activeMobileQuestion)}>
            {props.t("questionnaire.mobile.previous")}
          </button>
          <button className="secondary-button" type="button" disabled={mobileQuestionIndex >= total - 1} onClick={() => selectMobileQuestion(visibleItems[mobileQuestionIndex + 1]?.key ?? activeMobileQuestion)}>
            {props.t("questionnaire.mobile.next")}
          </button>
        </div>
      )}
      {!locked && (
        <QuestionnaireSubmitBlock
          questionnaire={props.questionnaire}
          submitState={submitState}
          submitErrorKey={submitErrorKey}
          allDone={allDone}
          missing={missingSummary}
          t={props.t}
          onSubmit={() => void submit()}
        />
      )}
    </main>
  );
}

export function QuestionnaireLoadingState(props: { t(key: string): string }) {
  return (
    <main className="questionnaire-state questionnaire-loading-shell" aria-live="polite">
      <div className="questionnaire-loading-heading">
        <div className="loading-pulse" />
        <div>
          <h1>{props.t("questionnaire.loadingTitle")}</h1>
          <p>{props.t("questionnaire.loadingSavedAnswers")}</p>
        </div>
      </div>
      <div className="questionnaire-loading-cards" aria-hidden="true">
        <span className="questionnaire-loading-card" />
        <span className="questionnaire-loading-card" />
      </div>
    </main>
  );
}

function clientQuestionText(item: QuestionItem, t: (key: string) => string): string {
  return item.key === "q1_02_twin_mode" ? t("questionnaire.clientCopy.q1TwinMode") : item.question;
}

/**
 * q2 lands on the strategy it just triggered; q1 hands over to the rail, which
 * opens the first step that is genuinely available (know-how in the normal
 * order). Neither questionnaire ends in silence any more (F47).
 */
export function questionnairePostSubmitTarget(questionnaire: QuestionnaireKey): QuestionnaireSubmitTarget {
  return questionnaire === "q2" ? "strategy" : "next";
}

/**
 * K09: how many blocking questions may be spelled out in full before the list
 * stops being a hint and becomes a second copy of the questionnaire. Named by
 * COUNT, not by viewport: 35 titles are a wall at every width, and the phone
 * only makes it obvious, because there the runner shows one question at a
 * time and this footer follows every single one of them (measured on g-q2 at
 * 390 px: 3 174 px of the 4 864 px page, 65 %). Up to this many the list IS
 * the answer - the client reads five titles and goes to fix them.
 */
export const REMAINING_INLINE_MAX = 5;

export function QuestionnaireSubmitBlock(props: {
  questionnaire: QuestionnaireKey;
  submitState: QuestionnaireSubmitState;
  submitErrorKey?: string;
  allDone: boolean;
  missing: ReadonlyArray<{ index: number; title: string }>;
  t(key: string): string;
  onSubmit(): void;
}) {
  if (props.submitState === "waiting" || props.submitState === "ready") {
    return (
      <div className="q-list-footer q-list-closed" role="status" data-questionnaire-closed={props.questionnaire}>
        <p className="q-list-locked">
          <span aria-hidden="true">{props.submitState === "ready" ? "✓" : "⏳"}</span>
          {props.t(props.submitState === "ready"
            ? "questionnaire.submittedClosed"
            : "questionnaire.submittedWaiting")}
        </p>
      </div>
    );
  }
  return (
    <div className="q-list-footer">
      {props.allDone && props.missing.length === 0 && <p className="q-list-alldone">{props.t("questionnaire.list.allDone")}</p>}
      {props.missing.length > 0 && (
        props.missing.length > REMAINING_INLINE_MAX ? (
          // The count is the sentence; the titles are the detail behind it.
          // Nothing is hidden that the client cannot open in one tap, and the
          // list itself is unchanged once opened.
          <details className="q-list-remaining q-list-remaining-fold">
            <summary>
              {props.t("questionnaire.list.remainingFold").replace("{count}", String(props.missing.length))}
            </summary>
            <ul className="q-list-remaining-items">
              {props.missing.map((item) => <li key={item.index}>{item.index}. {item.title}</li>)}
            </ul>
          </details>
        ) : (
          <div className="q-list-remaining">
            <p>{props.t("questionnaire.list.remaining")}</p>
            <ul className="q-list-remaining-items">
              {props.missing.map((item) => <li key={item.index}>{item.index}. {item.title}</li>)}
            </ul>
          </div>
        )
      )}
      {props.submitState === "error" && <p className="error-message" role="alert">{props.t(props.submitErrorKey ?? "questionnaire.submitTechnicalError")}</p>}
      {props.submitState === "dossier_pending" && <p className="message success-message" role="status">{props.t("questionnaire.submitDossierPending")}</p>}
      <button
        className="primary-button"
        type="button"
        data-questionnaire-submit={props.questionnaire}
        disabled={props.submitState === "submitting" || props.missing.length > 0}
        onClick={props.onSubmit}
      >
        {props.submitState === "submitting" ? props.t("questionnaire.submitting") : props.t("questionnaire.submitAndContinue")}
      </button>
    </div>
  );
}

function ReadOnlyAnswer(props: { draft: AnswerDraft; t(key: string): string }) {
  const entries = Array.isArray(props.draft.value.entries)
    ? (props.draft.value.entries as Array<Record<string, unknown>>)
    : [];
  // own_links/example_links (F76) read the same way the plain links list does.
  const links = ["links", "own_links", "example_links"].flatMap((key) => {
    const list = props.draft.value[key];
    return Array.isArray(list) ? list.filter((value): value is string => typeof value === "string") : [];
  });
  const files = Array.isArray(props.draft.value.attachments)
    ? (props.draft.value.attachments as Array<Record<string, unknown>>)
    : [];
  const body = entries.length > 0
    ? ""
    : answerBody({ transcript: props.draft.transcript }, props.draft.value, props.t);
  return (
    <div className="q-readonly-answer" aria-label={props.t("questionnaire.list.savedAnswer")}>
      {/* F87: the explicit "leave it to the team" answer reads back as itself. */}
      {props.draft.value.defer_to_team === true && <p>{props.t("questionnaire.design.deferNote")}</p>}
      {/* F71b: the explicit "I have no photos" answer reads back as itself. */}
      {props.draft.value.no_photos === true && <p>{props.t("questionnaire.photos.noPhotosNote")}</p>}
      {body && <p>{body}</p>}
      {entries.length > 0 && (
        <ol className="readonly-repeat">
          {entries.map((entry, index) => (
            <li key={String(entry.id ?? index)}>
              <p>{stringValue(entry.text)}</p>
              {typeof entry.real_vs_estimate === "string" && entry.real_vs_estimate && (
                <small>{props.t(`questionnaire.options.${String(entry.real_vs_estimate)}`)}</small>
              )}
            </li>
          ))}
        </ol>
      )}
      {links.length > 0 && (
        <ul>
          {links.map((link) => <li key={link}><a className="quiet-link break-text" href={link} target="_blank" rel="noreferrer">{link}</a></li>)}
        </ul>
      )}
      {files.length > 0 && (
        <ul>
          {files.map((file, index) => <li key={String(file.id ?? index)}>{String(file.name ?? props.t("questionnaire.list.savedFile"))}</li>)}
        </ul>
      )}
    </div>
  );
}

export async function routeQuestionnaireSubmit(
  testMode: boolean,
  onTestAdvance: (() => void | Promise<void>) | undefined,
  persist: () => Promise<void>
): Promise<void> {
  if (testMode) {
    await onTestAdvance?.();
    return;
  }
  await persist();
}

function QuestionField(props: {
  item: QuestionItem;
  draft: AnswerDraft;
  facts: PrefillFact[];
  scope: "q1_voice" | "q2_voice";
  currency: MarketCurrency;
  csrfToken: string;
  t(key: string): string;
  update(update: Partial<AnswerDraft>): void;
  onRecordingStateChange(state: VoiceRecordingState): void;
}) {
  const { item, draft } = props;
  if (item.type === "repeat_voice_or_text") {
    return <RepeatVoiceField {...props} />;
  }
  if (item.type === "confirm") {
    const decided = factActionsFrom(draft.value);
    const savedCorrection = typeof draft.value.correction === "string" ? draft.value.correction : "";
    if (props.facts.length === 0) {
      return (
        <div className="prefill-list">
          <article className="prefill-card prefill-empty">
            <p>{props.t("questionnaire.prefillEmpty")}</p>
          </article>
        </div>
      );
    }
    return (
      <div className="prefill-list">
        <p className="prefill-intro">{props.t("questionnaire.prefillIntro")}</p>
        {props.facts.map((fact) => {
          const pending = draft.factUpdates.find((update) => update.factId === fact.id);
          const action = pending?.action ?? decided[fact.id];
          const correctionText = pending?.text ?? savedCorrection;
          return (
            <article className={`prefill-card${action ? ` decided decided-${action}` : ""}`} key={fact.id} data-decided={action ?? ""}>
              <span className="prefill-category">{props.t(`questionnaire.factCategory.${fact.category}`)}</span>
              <p>{fact.effectiveText}</p>
              <small className="prefill-source">{props.t("questionnaire.foundOnline")}: {sourceLabel(fact.source, props.t)}</small>
              <div className="prefill-actions" role="group" aria-label={fact.effectiveText}>
                <button type="button" className={action === "confirm" ? "active" : ""} onClick={() => setFactAction(props, fact, "confirm")}>{props.t("questionnaire.fact.confirm")}</button>
                <button type="button" className={action === "correct" ? "active" : ""} onClick={() => setFactAction(props, fact, "correct")}>{props.t("questionnaire.fact.correct")}</button>
                <button type="button" className={action === "irrelevant" ? "active" : ""} onClick={() => setFactAction(props, fact, "irrelevant")}>{props.t("questionnaire.fact.irrelevant")}</button>
              </div>
              {action === "correct" && (
                <VoiceableInput
                  mini
                  scope={props.scope}
                  value={correctionText}
                  attachmentId={pending?.attachmentId}
                  csrfToken={props.csrfToken}
                  t={props.t}
                  onRecordingStateChange={props.onRecordingStateChange}
                  onTextChange={(text) => setFactAction(props, fact, "correct", text)}
                  onAttachmentReady={(attachment) => setFactAction(props, fact, "correct", pending?.text ?? correctionText, attachment.attachmentId)}
                  onAttachmentClear={() => setFactAction(props, fact, "correct", pending?.text ?? correctionText)}
                />
              )}
            </article>
          );
        })}
        <div className="prefill-voice">
          <p className="prefill-voice-prompt">{props.t("questionnaire.prefillVoicePrompt")}</p>
          <VoiceableInput
            scope={props.scope}
            value={stringValue(draft.value.text)}
            attachmentId={draft.attachmentId ?? undefined}
            csrfToken={props.csrfToken}
            t={props.t}
            onRecordingStateChange={props.onRecordingStateChange}
            onTextChange={(text) => props.update({ value: { ...draft.value, text } })}
            onAttachmentReady={(attachment) => props.update({ attachmentId: attachment.attachmentId, transcriptStatus: attachment.transcriptStatus })}
            onAttachmentClear={() => props.update({ attachmentId: null, transcriptStatus: null })}
          />
        </div>
      </div>
    );
  }
  // The profile photo is a FILE question, not a talking question (Pavel
  // 2026-08-05, F44): a mic and a text box next to "upload your photo" only
  // invite an answer nobody can use. Upload or a link to the photo, nothing
  // else. The stored value shape is unchanged (links[] + attachments[]).
  if (item.key === "q1_13_profile_photo") {
    return (
      <div className="structured-fields">
        <ProofExtras
          draft={draft}
          csrfToken={props.csrfToken}
          t={props.t}
          update={props.update}
          scope="expert_photo"
          labelPrefix="questionnaire.profilePhoto"
          maxFiles={1}
        />
      </div>
    );
  }
  // F71 (Pavel 2026-08-06): the sales photos question is a pure collection -
  // several links, several uploads with previews. No mic, no text box; a note
  // nobody can print on a sales page only invited unusable answers. Legacy v1
  // keeps the talking branch below: its completeness still honors the note.
  if (item.key === "q2_19_sales_photos" && item.questionVersion !== 1) {
    // F71b: a client who has no photos needs a way OUT of a pure collection,
    // or the questionnaire is unfinishable (Prenosilova 2026-09-04). Same
    // shape as the design question's "Nechám to na vás" (F87): one explicit
    // toggle that completes the question, with the uploads left open so the
    // client can still change their mind and add photos later.
    const noPhotos = draft.value.no_photos === true;
    return (
      <div className="structured-fields">
        <div className="design-defer">
          <button
            type="button"
            className={`secondary-button compact-button design-defer-toggle${noPhotos ? " active" : ""}`}
            aria-pressed={noPhotos}
            onClick={() => {
              const next = { ...draft.value };
              if (noPhotos) delete next.no_photos;
              else next.no_photos = true;
              props.update({ value: next });
            }}
          >
            {noPhotos ? "✓ " : ""}{props.t("questionnaire.photos.noPhotosButton")}
          </button>
          {noPhotos && <p className="design-defer-note">{props.t("questionnaire.photos.noPhotosNote")}</p>}
        </div>
        <ProofExtras
          draft={draft}
          csrfToken={props.csrfToken}
          t={props.t}
          update={props.update}
          scope="expert_photo"
          labelPrefix="questionnaire.photos"
          multiple
          preview
        />
      </div>
    );
  }
  // The lexicon is a LIST of exact spellings, so it is typed, never dictated:
  // a term that Scribe misheard once would feed the very keyterms meant to
  // stop it mishearing it (Tomas Pohl 2026-09-17: "textfield kam expert vypise
  // rucne, zadny voice tady"). Same stored shape ({text}) as before, one term
  // per line or comma separated; the training run copies it into the Buddy's
  // Transcription settings.
  if (item.key === "q1_17_lexicon" || item.key === "q2_65_lexicon") {
    return (
      <div className="structured-fields" data-lexicon-field={item.key}>
        <p className="q-help">{props.t("questionnaire.help.lexicon")}</p>
        <label>
          {props.t("questionnaire.fields.lexicon")}
          <textarea
            rows={6}
            value={stringValue(draft.value.text)}
            placeholder={props.t("questionnaire.fields.lexiconPlaceholder")}
            aria-label={item.question}
            onChange={(event) => props.update({ value: { ...draft.value, text: event.target.value } })}
          />
        </label>
      </div>
    );
  }
  if (item.type === "voice_or_text") {
    const textField = item.key === "q1_09_first_message" ? "final_text" : "text";
    return (
      <div className="structured-fields">
        <VoiceableInput
          scope={props.scope}
          value={stringValue(draft.value[textField])}
          attachmentId={draft.attachmentId ?? undefined}
          required={item.required}
          ariaLabel={item.question}
          csrfToken={props.csrfToken}
          t={props.t}
          onRecordingStateChange={props.onRecordingStateChange}
          onTextChange={(text) => props.update({ value: { ...draft.value, [textField]: text } })}
          onAttachmentReady={(attachment) => props.update({ attachmentId: attachment.attachmentId, transcriptStatus: attachment.transcriptStatus })}
          onAttachmentClear={() => props.update({ attachmentId: null, transcriptStatus: null })}
        />
        {/* References arrive in every shape (Pavel): besides talking, the
            client pastes links and uploads screenshots so the team can use
            the proof systematically. Stored inside the answer value. */}
        {item.key === "q2_06_proof" && (
          <ProofExtras draft={draft} csrfToken={props.csrfToken} t={props.t} update={props.update} />
        )}
        {/* Legacy v1 photos keep their original extras next to the mic. */}
        {item.key === "q2_19_sales_photos" && (
          <ProofExtras draft={draft} csrfToken={props.csrfToken} t={props.t} update={props.update} scope="expert_photo" labelPrefix="questionnaire.photos" />
        )}
        {/* F77: the client's own marketing materials (image or PDF) plus
            links teach the pipeline their writing voice. */}
        {item.key === "q2_54_writing_style_samples" && (
          <ProofExtras
            draft={draft}
            csrfToken={props.csrfToken}
            t={props.t}
            update={props.update}
            scope="brand_material"
            labelPrefix="questionnaire.writingSamples"
            accept={BRAND_UPLOAD_MIME_TYPES}
            multiple
            preview
          />
        )}
        {(item.key === "q2_04_client_stories" || item.key === "q2_06_proof") && (
          <label className="permission-field">
            <input
              type="checkbox"
              checked={draft.value.permission_confirmed === true}
              onChange={(event) => props.update({ value: { ...draft.value, permission_confirmed: event.target.checked } })}
            />
            {props.t("questionnaire.permissionConfirmed")}
          </label>
        )}
      </div>
    );
  }
  if (item.key === "q1_10_google_account") {
    return (
      <div className="structured-fields">
        <p className="q-help">{props.t("questionnaire.help.googleAccount")}</p>
        <label>{props.t("questionnaire.fields.googleAccount")}
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={stringValue(draft.value.email)}
            onChange={(event) => props.update({ value: { ...draft.value, email: event.target.value } })}
          />
        </label>
      </div>
    );
  }
  if (item.key === "q1_07_buddy_name") {
    return (
      <div className="structured-fields">
        <label>{props.t("questionnaire.fields.buddyName")}<input value={stringValue(draft.value.name)} onChange={(event) => props.update({ value: { ...draft.value, name: event.target.value } })} /></label>
        <label>{props.t("questionnaire.fields.username")}<input value={stringValue(draft.value.username)} onChange={(event) => props.update({ value: { ...draft.value, username: event.target.value, availability: "manual_check" } })} /></label>
        <UsernameAvailability username={stringValue(draft.value.username)} t={props.t} />
      </div>
    );
  }
  return (
    <ChoiceFields
      itemKey={item.key}
      value={draft.value}
      t={props.t}
      update={(value) => props.update({ value })}
      scope={props.scope}
      currency={props.currency}
      csrfToken={props.csrfToken}
      onRecordingStateChange={props.onRecordingStateChange}
    />
  );
}

function RepeatVoiceField(props: Parameters<typeof QuestionField>[0]) {
  const entries = Array.isArray(props.draft.value.entries)
    ? (props.draft.value.entries as Array<Record<string, unknown>>)
    : [];
  const updateEntries = (next: Array<Record<string, unknown>>) =>
    props.update({ value: { entries: next } });
  const updateEntry = (index: number, update: Record<string, unknown>) =>
    updateEntries(entries.map((entry, entryIndex) => entryIndex === index ? { ...entry, ...update } : entry));
  return (
    <div className="repeat-field">
      {entries.map((entry, index) => (
        <article className="repeat-entry" key={String(entry.id)}>
          <div className="repeat-entry-head">
            <strong>{props.t("questionnaire.repeat.item").replace("{number}", String(index + 1))}</strong>
            <button className="quiet-link" type="button" onClick={() => updateEntries(entries.filter((_, entryIndex) => entryIndex !== index))}>
              {props.t("questionnaire.repeat.remove")}
            </button>
          </div>
          {props.item.repeatVariant === "real_vs_estimate" && (
            <div className="segmented repeat-kind" role="radiogroup" aria-label={props.t("questionnaire.fields.real_vs_estimate")}>
              {["real", "estimate"].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={entry.real_vs_estimate === option ? "active" : ""}
                  aria-pressed={entry.real_vs_estimate === option}
                  onClick={() => updateEntry(index, { real_vs_estimate: option })}
                >
                  {props.t(`questionnaire.options.${option}`)}
                </button>
              ))}
            </div>
          )}
          <VoiceableInput
            mini
            scope={props.scope}
            value={stringValue(entry.text)}
            attachmentId={stringValue(entry.attachmentId) || undefined}
            required
            ariaLabel={`${props.item.question} ${index + 1}`}
            csrfToken={props.csrfToken}
            t={props.t}
            onRecordingStateChange={props.onRecordingStateChange}
            onTextChange={(text) => updateEntry(index, { text })}
            onAttachmentReady={(attachment) => updateEntry(index, {
              attachmentId: attachment.attachmentId,
              transcriptStatus: attachment.transcriptStatus,
              ...(attachment.transcript ? { text: attachment.transcript } : {})
            })}
            onAttachmentClear={() => updateEntry(index, { attachmentId: undefined, transcriptStatus: undefined })}
          />
        </article>
      ))}
      <button
        className="secondary-button compact-button repeat-add"
        type="button"
        onClick={() => updateEntries([...entries, {
          id: crypto.randomUUID(),
          text: "",
          ...(props.item.repeatVariant === "real_vs_estimate" ? { real_vs_estimate: "estimate" } : {})
        }])}
      >
        {props.t("questionnaire.repeat.add")}
      </button>
      <small className="q-help">
        {props.t("questionnaire.repeat.minimum").replace("{count}", String(props.item.minEntries ?? 1))}
      </small>
    </div>
  );
}

// Live Telegram username check with a short debounce; the team still does the
// final verification before creating the bot.
function UsernameAvailability(props: { username: string; t(key: string): string }) {
  const [status, setStatus] = useState<"idle" | "checking" | "probably_free" | "taken" | "invalid" | "unknown">("idle");
  useEffect(() => {
    const name = props.username.trim().replace(/^@/, "");
    if (!name) { setStatus("idle"); return; }
    if (!/^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(name)) { setStatus("invalid"); return; }
    setStatus("checking");
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/telegram/username-check?name=${encodeURIComponent(name)}`, { credentials: "include" });
        const body = await response.json() as { status?: string };
        setStatus((body.status === "taken" || body.status === "probably_free" || body.status === "invalid") ? body.status : "unknown");
      } catch {
        setStatus("unknown");
      }
    }, 700);
    return () => window.clearTimeout(timer);
  }, [props.username]);
  if (status === "idle" || status === "unknown") return <small>{props.t("questionnaire.usernameManual")}</small>;
  if (status === "checking") return <small className="username-status">{props.t("questionnaire.username.checking")}</small>;
  if (status === "invalid") return <small className="username-status username-invalid">{props.t("questionnaire.username.invalid")}</small>;
  if (status === "taken") return <small className="username-status username-taken">✗ {props.t("questionnaire.username.taken")}</small>;
  return <small className="username-status username-free">✓ {props.t("questionnaire.username.free")}</small>;
}

function ChoiceFields(props: {
  itemKey: string;
  value: Record<string, unknown>;
  t(key: string): string;
  update(value: Record<string, unknown>): void;
  scope: "q1_voice" | "q2_voice";
  currency: MarketCurrency;
  csrfToken: string;
  onRecordingStateChange(state: VoiceRecordingState): void;
}) {
  const options = choiceOptions(props.itemKey);
  // F65: follow-up prose fields speak - same mic orb, transcript into the
  // field, recording archived under `<field>_recording` inside the value.
  const voiceField = (field: string) => (
    <VoiceTextField
      field={field}
      value={props.value}
      update={props.update}
      t={props.t}
      scope={props.scope}
      csrfToken={props.csrfToken}
      onRecordingStateChange={props.onRecordingStateChange}
    />
  );
  if (props.itemKey === "q1_08_voice_and_meditation") {
    return (
      <div className="structured-fields">
        <p className="q-help">{props.t("questionnaire.help.voiceAndMeditation")}</p>
        <SelectField field="voice" options={["clone", "other"]} {...props} />
        <BooleanField field="meditation" {...props} />
      </div>
    );
  }
  if (props.itemKey === "q2_12_audience_size") {
    return <div className="structured-fields"><SelectField field="email_range" options={options} {...props} /><SelectField field="audience_range" options={options} {...props} /></div>;
  }
  if (props.itemKey === "q2_10_price") {
    const mode = stringValue(props.value.mode);
    return <div className="structured-fields"><SelectField field="mode" options={["range", "recommend"]} {...props} />{mode === "range" && <><label>{props.t("questionnaire.fields.priceMin")}<input type="number" value={numberValue(props.value.min)} onChange={(event) => props.update({ ...props.value, min: Number(event.target.value), currency: props.currency })} /></label><label>{props.t("questionnaire.fields.priceMax")}<input type="number" value={numberValue(props.value.max)} onChange={(event) => props.update({ ...props.value, max: Number(event.target.value), currency: props.currency })} /></label></>}</div>;
  }
  if (props.itemKey === "q2_16_stripe") return <BooleanField field="has_stripe" {...props} />;
  if (props.itemKey === "q2_28_acquisition") {
    const selected = Array.isArray(props.value.channels) ? props.value.channels.map(String) : [];
    const choices = ["paid_ads", "own_audience", "build_socials", "acquisition_recommend"];
    return (
      <div className="structured-fields">
        <div className="choice-field multi-choice">
          <span className="choice-field-label">{props.t("questionnaire.fields.channels")}</span>
          <div className="segmented">
            {choices.map((option) => (
              <button
                key={option}
                type="button"
                className={selected.includes(option) ? "active" : ""}
                aria-pressed={selected.includes(option)}
                onClick={() => props.update({
                  ...props.value,
                  channels: selected.includes(option)
                    ? selected.filter((entry) => entry !== option)
                    : [...selected, option]
                })}
              >
                {props.t(`questionnaire.options.${option}`)}
              </button>
            ))}
          </div>
        </div>
        {/* F75: budget AND weekly time, both spoken or typed. */}
        {voiceField("budget")}
        {voiceField("marketing_time_weekly")}
      </div>
    );
  }
  // F76: structured design collection; legacy text answers are migrated on
  // the first edit (shared/design-inputs).
  if (props.itemKey === "q2_52_design_inputs") {
    return (
      <DesignInputsField
        value={props.value}
        update={props.update}
        csrfToken={props.csrfToken}
        scope={props.scope}
        t={props.t}
        onRecordingStateChange={props.onRecordingStateChange}
      />
    );
  }
  if (props.itemKey.startsWith("q2_m")) {
    const selected = stringValue(props.value.selected);
    return (
      <div className="structured-fields">
        <SelectField field="selected" options={options} {...props} />
        {/* F80a: how the expert really treats their list feeds the e-mail
            warm-up plan instead of an estimate. */}
        {props.itemKey === "q2_m01_database" && selected !== "" && selected !== "no_database" && voiceField("list_engagement")}
        {props.itemKey === "q2_m02_socials" && selected === "community" && voiceField("community_where")}
        {props.itemKey === "q2_m06_campaign" && selected === "yes" && voiceField("campaign_type")}
        {/* F66: the order form link is no longer collected here - q2_m08
            carries the client's existing pages and materials. */}
        {props.itemKey === "q2_m07_payments" && selected === "no_stripe" && (
          <>
            <SelectField field="payment_method" options={["fapi", "other_gateway", "none"]} {...props} />
            {props.value.payment_method === "other_gateway" && <TextField field="other_gateway" {...props} />}
          </>
        )}
        {props.itemKey === "q2_m08_promo" && selected === "yes" && voiceField("materials")}
      </div>
    );
  }
  if (props.itemKey === "q2_24_brand_face") {
    return <SelectField field="mode" options={["owner", "standalone", "brand_recommend"]} {...props} />;
  }
  const field = props.itemKey === "q1_02_twin_mode" ? "mode" : "model";
  return <SelectField field={field} options={options} {...props} />;
}

function TextField(props: {
  field: string;
  value: Record<string, unknown>;
  t(key: string): string;
  update(value: Record<string, unknown>): void;
  type?: "text" | "url";
  multiline?: boolean;
}) {
  const label = props.t(`questionnaire.fields.${props.field}`);
  return (
    <label>
      {label}
      {props.multiline ? (
        <textarea
          aria-label={label}
          value={stringValue(props.value[props.field])}
          onChange={(event) => props.update({ ...props.value, [props.field]: event.target.value })}
        />
      ) : (
        <input
          type={props.type ?? "text"}
          aria-label={label}
          value={stringValue(props.value[props.field])}
          onChange={(event) => props.update({ ...props.value, [props.field]: event.target.value })}
        />
      )}
    </label>
  );
}

// Choices render as segmented pills, never as a native grey dropdown or
// radio (design law: no UA controls in the client dashboard).
function SelectField(props: { field: string; options: string[]; value: Record<string, unknown>; t(key: string): string; update(value: Record<string, unknown>): void; itemKey: string }) {
  const current = stringValue(props.value[props.field]);
  return (
    <div className="choice-field" role="radiogroup" aria-label={props.t(`questionnaire.fields.${props.field}`)}>
      <span className="choice-field-label">{props.t(`questionnaire.fields.${props.field}`)}</span>
      <div className="segmented">
        {props.options.map((option) => (
          <button
            key={option}
            type="button"
            className={current === option ? "active" : ""}
            aria-pressed={current === option}
            onClick={() => props.update(questionnaireChoiceValue(props.itemKey, props.field, option, props.value))}
          >
            {props.t(`questionnaire.options.${option}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function questionnaireChoiceValue(
  itemKey: string,
  field: string,
  option: string,
  current: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...current, [field]: option };
  if (field === "selected") {
    if (itemKey === "q2_m01_database" && option === "no_database") {
      delete next.list_engagement;
      delete next.list_engagement_recording;
    }
    if (itemKey === "q2_m02_socials" && option !== "community") {
      delete next.community_where;
      delete next.community_where_recording;
    }
    if (itemKey === "q2_m06_campaign" && option !== "yes") {
      delete next.campaign_type;
      delete next.campaign_type_recording;
    }
    if (itemKey === "q2_m07_payments" && option !== "no_stripe") {
      delete next.payment_method;
      delete next.other_gateway;
      delete next.order_form_url;
    }
    if (itemKey === "q2_m08_promo" && option !== "yes") {
      delete next.materials;
      delete next.materials_recording;
    }
  }
  if (itemKey === "q2_m07_payments" && field === "payment_method") {
    if (option !== "other_gateway") delete next.other_gateway;
    if (option === "none") delete next.order_form_url;
  }
  return next;
}

function BooleanField(props: { field: string; value: Record<string, unknown>; t(key: string): string; update(value: Record<string, unknown>): void }) {
  const current = props.value[props.field];
  return (
    <div className="choice-field" role="radiogroup" aria-label={props.t(`questionnaire.fields.${props.field}`)}>
      <span className="choice-field-label">{props.t(`questionnaire.fields.${props.field}`)}</span>
      <div className="segmented">
        <button type="button" className={current === true ? "active" : ""} aria-pressed={current === true} onClick={() => props.update({ ...props.value, [props.field]: true })}>{props.t("questionnaire.yes")}</button>
        <button type="button" className={current === false ? "active" : ""} aria-pressed={current === false} onClick={() => props.update({ ...props.value, [props.field]: false })}>{props.t("questionnaire.no")}</button>
      </div>
    </div>
  );
}


export function factActionsFrom(value: Record<string, unknown>): Record<string, "confirm" | "correct" | "irrelevant"> {
  const raw = value.fact_actions;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const decisions: Record<string, "confirm" | "correct" | "irrelevant"> = {};
  for (const [factId, action] of Object.entries(raw as Record<string, unknown>)) {
    if (action === "confirm" || action === "correct" || action === "irrelevant") decisions[factId] = action;
  }
  return decisions;
}

// Decisions must accumulate in value.fact_actions: pending factUpdates are
// cleared after every autosave, so deriving confirmed/reviewed from them alone
// forgets earlier facts and permanently blocks submission (finding F9).
export function factDecisionUpdate(input: {
  itemKey: string;
  value: Record<string, unknown>;
  factUpdates: AnswerDraft["factUpdates"];
  facts: PrefillFact[];
  fact: PrefillFact;
  action: "confirm" | "correct" | "irrelevant";
  text?: string;
  attachmentId?: string;
}): Pick<AnswerDraft, "factUpdates" | "sourceFactIds" | "value"> {
  const next = input.factUpdates.filter((update) => update.factId !== input.fact.id);
  next.push({
    factId: input.fact.id,
    action: input.action,
    ...(input.text !== undefined ? { text: input.text } : {}),
    ...(input.attachmentId ? { attachmentId: input.attachmentId } : {})
  });
  const decided = { ...factActionsFrom(input.value), [input.fact.id]: input.action };
  const savedCorrection = typeof input.value.correction === "string" ? input.value.correction : null;
  const correction = next.find((update) => update.action === "correct")?.text
    ?? (Object.values(decided).includes("correct") ? savedCorrection : null);
  return {
    factUpdates: next,
    sourceFactIds: input.facts.map((item) => item.id),
    value: {
      confirmed: input.facts.length > 0 && input.facts.every((item) => decided[item.id] === "confirm"),
      reviewed: input.facts.length > 0 && input.facts.every((item) => decided[item.id] !== undefined),
      [input.itemKey === "q2_08_products_prices" ? "product_fact_keys" : "fact_keys"]: input.facts.map((item) => item.factKey),
      correction,
      fact_actions: decided
    }
  };
}

function setFactAction(
  props: Parameters<typeof QuestionField>[0],
  fact: PrefillFact,
  action: "confirm" | "correct" | "irrelevant",
  text?: string,
  attachmentId?: string
) {
  props.update(factDecisionUpdate({
    itemKey: props.item.key,
    value: props.draft.value,
    factUpdates: props.draft.factUpdates,
    facts: props.facts,
    fact,
    action,
    text,
    attachmentId
  }));
}

function defaultValue(item: QuestionItem): Record<string, unknown> {
  const itemKey = item.key;
  if (item.type === "repeat_voice_or_text") {
    return {
      entries: Array.from({ length: item.minEntries ?? 1 }, () => ({
        id: crypto.randomUUID(),
        text: "",
        ...(item.repeatVariant === "real_vs_estimate" ? { real_vs_estimate: "estimate" } : {})
      }))
    };
  }
  if (itemKey === "q1_09_first_message") return { proposal: "", final_text: "" };
  if (itemKey.endsWith("business_essence_and_value") || itemKey.endsWith("target_client") || itemKey.endsWith("voice_and_impression") ||
    itemKey.endsWith("method_or_framework") || itemKey.endsWith("boundaries") || itemKey.endsWith("first_audience") ||
    itemKey.endsWith("first_week_result") || itemKey.endsWith("proof")) return { text: "" };
  return {};
}

function choiceOptions(itemKey: string): string[] {
  if (itemKey === "q1_02_twin_mode") return ["clone", "knowledge_expert"];
  if (itemKey === "q2_12_audience_size") return ["under_1000", "1000_4999", "5000_19999", "20000_plus"];
  if (itemKey === "q2_15_sales_model") return ["two_day_trial", "webinar", "email_campaign", "inside_program", "recommend"];
  if (itemKey === "q2_m01_database") {
    return ["no_database", "database_under_1000", "database_1000_5000", "database_5000_20000", "database_more"];
  }
  if (itemKey === "q2_m02_socials") return ["inactive", "profiles_only", "active", "community"];
  if (itemKey === "q2_m03_allies") return ["yes", "no", "unknown"];
  if (itemKey === "q2_m07_payments") return ["has_stripe", "no_stripe"];
  if (["q2_m04_practice", "q2_m05_infoproducts", "q2_m06_campaign", "q2_m08_promo"].includes(itemKey)) {
    return ["yes", "no"];
  }
  return [];
}

// References in every shape (Pavel's annotation): links to public proof and
// uploaded files live inside the answer value, next to the spoken/written
// part, so the team can use them systematically. The upload itself (multi
// select, previews, named failure reasons) lives in FileListField.
function ProofExtras(props: {
  draft: AnswerDraft;
  csrfToken: string;
  t(key: string): string;
  update(update: Partial<AnswerDraft>): void;
  scope?: string;
  labelPrefix?: string;
  maxFiles?: number;
  accept?: readonly string[];
  multiple?: boolean;
  preview?: boolean;
}) {
  const scope = props.scope ?? "proof_screenshot";
  const labelPrefix = props.labelPrefix ?? "questionnaire.proof";
  const [link, setLink] = useState("");
  const links = Array.isArray(props.draft.value.links) ? props.draft.value.links.map(String) : [];
  const linkValid = /^https?:\/\/\S+\.\S+/.test(link.trim());

  function addLink() {
    const trimmed = link.trim();
    if (!linkValid || links.includes(trimmed)) return;
    props.update({ value: { ...props.draft.value, links: [...links, trimmed] } });
    setLink("");
  }

  function removeLink(target: string) {
    props.update({ value: { ...props.draft.value, links: links.filter((item) => item !== target) } });
  }

  return (
    <div className="proof-extras">
      <div className="proof-links">
        <label htmlFor="proof-link">{props.t(`${labelPrefix}.linksLabel`)}</label>
        {links.length > 0 && (
          <ul className="proof-item-list">
            {links.map((item) => (
              <li key={item}>
                <a className="quiet-link break-text" href={item} target="_blank" rel="noreferrer">{item}</a>
                <button className="quiet-link proof-remove" type="button" onClick={() => removeLink(item)}>{props.t(`${labelPrefix}.remove`)}</button>
              </li>
            ))}
          </ul>
        )}
        <div className="proof-link-row">
          <input
            id="proof-link"
            type="url"
            inputMode="url"
            placeholder="https://"
            value={link}
            onChange={(event) => setLink(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addLink(); } }}
          />
          <button className="secondary-button compact-button" type="button" disabled={!linkValid} onClick={addLink}>
            {props.t(`${labelPrefix}.addLink`)}
          </button>
        </div>
      </div>
      <FileListField
        value={props.draft.value}
        update={(value) => props.update({ value })}
        t={props.t}
        csrfToken={props.csrfToken}
        scope={scope}
        labelPrefix={labelPrefix}
        accept={props.accept}
        maxFiles={props.maxFiles}
        multiple={props.multiple}
        preview={props.preview}
      />
    </div>
  );
}

function sourceLabel(source: unknown, t: (key: string) => string): string {
  if (source && typeof source === "object" && !Array.isArray(source)) {
    const value = (source as Record<string, unknown>).url;
    if (typeof value === "string") return value;
  }
  return t("questionnaire.sourceUnknown");
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number | "" {
  return typeof value === "number" ? value : "";
}

function localDraftKey(namespace: string, questionnaire: string, itemKey: string): string {
  return `${namespace}:${questionnaire}:${itemKey}`;
}

async function openDraftDb(): Promise<IDBDatabase | null> {
  if (!("indexedDB" in window)) return null;
  return new Promise((resolve) => {
    const request = indexedDB.open("launchpad-questionnaire-drafts", 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("drafts")) request.result.createObjectStore("drafts");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function readLocalDraft(key: string): Promise<AnswerDraft | null> {
  const db = await openDraftDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const transaction = db.transaction("drafts", "readonly");
    const request = transaction.objectStore("drafts").get(key);
    request.onsuccess = () => resolve(request.result as AnswerDraft | null);
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => db.close();
  });
}

async function writeLocalDraft(key: string, value: AnswerDraft): Promise<void> {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const transaction = db.transaction("drafts", "readwrite");
    transaction.objectStore("drafts").put(value, key);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); resolve(); };
  });
}

async function deleteLocalDraft(key: string): Promise<void> {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const transaction = db.transaction("drafts", "readwrite");
    transaction.objectStore("drafts").delete(key);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); resolve(); };
  });
}

export async function clearQuestionnaireDrafts(namespace: string): Promise<void> {
  const db = await openDraftDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const transaction = db.transaction("drafts", "readwrite");
    const store = transaction.objectStore("drafts");
    const request = store.getAllKeys();
    request.onsuccess = () => {
      for (const key of request.result) {
        if (typeof key === "string" && key.startsWith(`${namespace}:`)) store.delete(key);
      }
    };
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); resolve(); };
  });
}
